import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { clamp01 } from '../../lib/format';
import { Button } from '../primitives/Button';
import { Countdown } from '../primitives/Countdown';
import { ProgressBar } from '../primitives/ProgressBar';
import { BoostIcon } from '../../icons/ui-icons';
import { EmptyState } from '../primitives/EmptyState';
import { ClockIcon } from '../../icons/ui-icons';

export type QueueKind = 'building' | 'research' | 'ship' | 'defense';

export interface QueueItemProps {
  /** Display name, e.g. "Titan-Schmelze". */
  name: ReactNode;
  /** Target level for buildings/research, or unit count for shipyard jobs. */
  level?: number;
  count?: number;
  kind?: QueueKind;
  icon?: ReactNode;
  /** Completion timestamp — drives both the countdown and the progress fill. */
  finishesAt: number | string | Date;
  /** Job start timestamp. Needed to compute elapsed progress. */
  startedAt: number | string | Date;
  /**
   * Chronos-crystal cost to FINISH this job outright. Omit to hide the action.
   * Always the full completion price — never a fraction of it.
   */
  finishCost?: number;
  onBoost?: () => void;
  onCancel?: () => void;
  /** Queued but not yet started — greyed out, no countdown. */
  pending?: boolean;
  className?: string;
}

const KIND_ACCENT = {
  building: 'titan',
  research: 'aether',
  ship: 'silicate',
  defense: 'danger',
} as const;

/**
 * One row of the build / research queue (GDD §4 wireframe, "ACTIVE QUEUE").
 *
 * Progress is derived from the start and finish timestamps rather than passed
 * in as a ratio, so the bar stays truthful across reloads and sleeping tabs —
 * the same anchoring rule the countdown uses.
 *
 * The finish button is a `premium` Button on purpose: it spends hard currency,
 * and paid actions must never be mistakable for free ones. The number on it is
 * the full price to complete the job, and pressing it opens a confirmation —
 * premium currency never leaves a player's account on a single click.
 */
export function QueueItem({
  name,
  level,
  count,
  kind = 'building',
  icon,
  finishesAt,
  startedAt,
  finishCost,
  onBoost,
  onCancel,
  pending = false,
  className,
}: QueueItemProps) {
  const start = toMs(startedAt);
  const end = toMs(finishesAt);
  const total = Math.max(1, end - start);
  const progress = pending ? 0 : clamp01((Date.now() - start) / total);
  const remainingSeconds = Math.max(0, (end - Date.now()) / 1000);

  return (
    <div
      className={cn(
        'rounded-control border border-edge-subtle bg-surface-2 px-3 py-2.5',
        pending && 'opacity-60',
        className,
      )}
    >
      {/*
        Two rows, not one. A single row has to share its width between the name,
        the level, the countdown and the boost button — and the name always
        loses, truncating to "Titan-S…" even in a wide panel. Giving the name its
        own row means it stays readable at every panel width, which matters
        because it is the only part a player scans for.
      */}
      <div className="flex items-baseline gap-1.5">
        {icon ? (
          <span className="shrink-0 self-center text-content-muted">{icon}</span>
        ) : null}
        <span className="min-w-0 flex-1 truncate font-display text-xs font-medium uppercase tracking-[0.08em] text-content">
          {name}
        </span>
        {level !== undefined ? (
          <span data-sg-numeric className="shrink-0 text-label text-content-muted">
            Stufe {level}
          </span>
        ) : null}
        {count !== undefined ? (
          <span data-sg-numeric className="shrink-0 text-label text-content-muted">
            ×{count}
          </span>
        ) : null}
      </div>

      <div className="mt-2 flex items-center gap-2">
        {pending ? (
          <span className="flex-1 font-display text-label uppercase text-content-muted">
            In Warteschlange
          </span>
        ) : (
          <>
            <ProgressBar
              value={progress}
              accent={KIND_ACCENT[kind]}
              size="xs"
              animated={remainingSeconds > 0}
              className="min-w-0 flex-1"
              aria-label="Baufortschritt"
            />
            <Countdown
              target={end}
              urgentBelow={300}
              className="shrink-0 text-xs font-semibold"
            />
          </>
        )}

        {!pending && finishCost !== undefined && onBoost ? (
          <Button
            variant="premium"
            size="sm"
            icon={<BoostIcon size={13} />}
            onClick={onBoost}
            title={`Sofort fertigstellen — ${finishCost} Chronos-Kristalle`}
            className="shrink-0"
          >
            {finishCost}
          </Button>
        ) : null}

        {onCancel ? (
          <Button variant="ghost" size="sm" onClick={onCancel} className="shrink-0">
            Abbrechen
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export interface BuildQueueProps {
  children?: ReactNode;
  /** Free queue slots — the Nano-Fabrikationszentrum unlocks more. */
  slots?: { used: number; total: number };
  emptyLabel?: string;
  className?: string;
}

/** Container for the queue rows, with slot capacity and an empty state. */
export function BuildQueue({ children, slots, emptyLabel, className }: BuildQueueProps) {
  const hasItems = Array.isArray(children) ? children.length > 0 : Boolean(children);

  return (
    <div className={cn('space-y-2', className)}>
      {slots ? (
        <div className="flex items-center justify-between px-0.5">
          <span className="font-display text-label-sm uppercase text-content-muted">
            Bauplätze
          </span>
          <span data-sg-numeric className="text-label text-content-secondary">
            {slots.used}/{slots.total}
          </span>
        </div>
      ) : null}

      {hasItems ? (
        children
      ) : (
        <EmptyState
          icon={<ClockIcon size={24} />}
          title={emptyLabel ?? 'Keine laufenden Aufträge'}
          description="Starte einen Ausbau, um die Warteschlange zu füllen."
        />
      )}
    </div>
  );
}

function toMs(value: number | string | Date): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Date.now() : parsed;
}
